import { useState } from 'react';
import { isValidInputTimeFormatController } from '../../labelling/utils';
import { TimeDisplayMode } from '../../types';
import { getFrameToTime, getTimeToFrame } from '../../utils/frame-time';
type FrameTimeStateParamsT = {
  currentFrame: number;
  maxFrames: number;
  frameRate: number | null | undefined;
  timeDisplayMode: TimeDisplayMode;
  setTimeDisplayMode: (mode: TimeDisplayMode) => void;
  onFrameChange: (frame: number) => void;
};

// abstracted out from FrameControls component for readability
export function useFrameTimeState({
  currentFrame,
  maxFrames,
  frameRate,
  timeDisplayMode,
  setTimeDisplayMode,
  onFrameChange
}: FrameTimeStateParamsT) {
  const [currentFrameLocal, setCurrentFrameLocal] = useState<string>(String(currentFrame + 1));
  const defaultCurrentTime = getFrameToTime({
    frame: currentFrame,
    maxFrames,
    frameRate
  });
  const [currentTimeLocal, setCurrentTimeLocal] = useState<string>(defaultCurrentTime);
  const [prevFrame, setPrevFrame] = useState(currentFrame);
  const [prevMaxFrames, setPrevMaxFrames] = useState(maxFrames);
  const [prevFrameRate, setPrevFrameRate] = useState(frameRate);
  if (currentFrame !== prevFrame || maxFrames !== prevMaxFrames || frameRate !== prevFrameRate) {
    setPrevFrame(currentFrame);
    setPrevMaxFrames(maxFrames);
    setPrevFrameRate(frameRate);
    setCurrentFrameLocal(String(currentFrame + 1));
    setCurrentTimeLocal(getFrameToTime({
      frame: currentFrame,
      maxFrames,
      frameRate
    }));
  }
  const currentTimeValid = isValidInputTimeFormatController(currentTimeLocal, maxFrames, getTimeToFrame(currentTimeLocal, frameRate));
  const currentFrameLocalNumber = Number(currentFrameLocal) - 1;
  const currentFrameValid = currentFrameLocal === '' || currentFrameLocalNumber >= 0 && currentFrameLocalNumber < maxFrames;
  const maxTime = getFrameToTime({
    frame: maxFrames - 1,
    maxFrames,
    frameRate
  });
  const isFrameMode = timeDisplayMode === TimeDisplayMode.FRAMES;
  const handleToggle = () => {
    setTimeDisplayMode(isFrameMode ? TimeDisplayMode.TIMESTAMP : TimeDisplayMode.FRAMES);
  };
  const handleFrameTimeChange = (val: string) => {
    if (!isFrameMode) {
      setCurrentTimeLocal(val);
      if (isValidInputTimeFormatController(val, maxFrames, getTimeToFrame(val, frameRate))) {
        onFrameChange(getTimeToFrame(val, frameRate));
      }
      return;
    }
    setCurrentFrameLocal(val);
    const frameNum = Number(val) - 1;
    if (val !== '' && frameNum >= 0 && frameNum < maxFrames) {
      onFrameChange(frameNum);
    }
  };
  return {
    currentValue: isFrameMode ? currentFrameLocal : currentTimeLocal,
    totalValue: isFrameMode ? String(maxFrames) : maxTime,
    label: isFrameMode ? 'Frame' : 'Time',
    toggleLabel: isFrameMode ? 'frames' : 'time',
    isError: isFrameMode ? !currentFrameValid : !currentTimeValid,
    currentFrameLocalNumber,
    handleToggle,
    handleFrameTimeChange
  };
}